import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleCheck, Clock } from "lucide-react";
import { useEffect, useId, useState } from "react";
import {
  apiFetch,
  ApiProblem,
  newIdempotencyKey,
  type ClockKind,
  type RecordedClockEvent,
  type StaffToday,
} from "../lib/api.ts";
import { useScreen } from "../lib/layout.ts";
import { useSession } from "../lib/session.tsx";
import { clockNextStep, clockStateChip } from "../lib/clock-steps.ts";
import { hoursText, signedHoursText } from "../lib/staff-display.ts";
import { cn } from "../lib/utils.ts";
import { TIME_TRACKING_PATH } from "../navigation.ts";
import { NoEmployeeNotice } from "./no-employee-notice.tsx";
import { Button } from "./ui/button.tsx";
import { NoticeCard } from "./ui/notice-card.tsx";
import { StateChip } from "./ui/state-chip.tsx";
import { PhoneActionBar } from "./ui/phone-action-bar.tsx";
import { ProgressRing, ringPercent } from "./ui/progress-ring.tsx";
import { IconCircle, TileHeading } from "./ui/tile.tsx";
import { buttonClass } from "./ui/variants.ts";
import { UndoToast } from "./undo-toast.tsx";

/** Abfrage „Heute“ der Zeiterfassung (Stempel-Karte, Zeiterfassung › Heute). */
export const STAFF_TODAY_KEY = ["staff", "today"] as const;

export const CLOCK_CARD_TEXTS = {
  title: "Zeiterfassung",
  offline: "Keine Verbindung – nicht gestempelt. Bitte erneut tippen.",
  failed: "Das hat nicht geklappt. Bitte erneut tippen.",
  done: (label: string, time: string) => `${label} gestempelt (${time})`,
  amend: "Nachtragen",
  /** Ring (Ticket 1.284): Ist von Soll; ab Soll „Soll erreicht · +0:15 h“. */
  ringLabel: "Heute, Ist von Soll",
  ofTarget: (actual: string, target: string) => `${actual} von ${target}`,
  reached: (balance: string) => `Soll erreicht · ${balance}`,
  today: "heute",
  /** Freude-Moment nach „Gehen“ (EXPERIENCE §1.2 S8). */
  farewell: (work: string) => `Feierabend · heute ${work}`,
} as const;

/** Knopf „Nachtragen“ in der Hinweis-Karte (Zweitknopf; Trefferfläche bei Touch ≥ 44 px). */
const AMEND_BUTTON = buttonClass("secondary", "sm");

/**
 * Sperre der Stempelknöpfe nach einem Stempel (Doppel-Tipp-Schutz der Oberfläche, keine fachliche Frist): Kommt die
 * Antwort schnell, steht am selben Platz schon die nächste Handlung („Gehen“); ein zweiter schneller Tipp darf sie nicht
 * auslösen. Der Server fängt nur denselben Knopf doppelt ab. „Rückgängig“ bleibt bedienbar.
 */
export const CLOCK_TAP_GUARD_MS = 1000;

const kindDone: Record<ClockKind, string> = {
  clock_in: "Kommen",
  break_start: "Pause",
  break_end: "Pause beendet",
  clock_out: "Gehen",
};

/** Uhrzeit Europe/Berlin `HH:MM` eines Zeitpunkts des Servers (nur Anzeige). */
function berlinTime(iso: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

/** „Heute“ der angemeldeten Person; läuft im Hintergrund nach, damit „heute 4:12 h“ weiterzählt. */
export function useStaffToday() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: STAFF_TODAY_KEY,
    queryFn: () => apiFetch<StaffToday>(getToken, "/api/v1/staff/me/today"),
    // Ohne Mitarbeiter-Eintrag (oder ausgeschieden) nicht minütlich nachfragen.
    refetchInterval: (query) =>
      query.state.data &&
      (query.state.data.day === null || query.state.data.blocked)
        ? false
        : 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hinweis „Gestern fehlt ‚Gehen‘ – bitte nachtragen“ (Story 22.5; gemeinsam für Stempel-Karte und Stempel-Chip, Ticket
 * 1.275). Mit `onAmend` öffnet „Nachtragen“ das Formular (Zeiterfassung › Heute), sonst führt der Link dorthin
 * (`?nachtragen=<Tag>`); `onNavigate` z. B. schließt vorher das Fenster des Chips.
 */
export function ForgottenNotice({
  forgotten,
  onAmend,
  onNavigate,
}: {
  forgotten: NonNullable<StaffToday["forgotten"]>;
  onAmend?: (date: string, opener: HTMLElement) => void;
  onNavigate?: () => void;
}) {
  // [RUDOLF 29.10.2026] Story 22.11: Der Packplatz stempelt nur – Nachträge macht das Büro (Hinweis ohne Knopf).
  const packingStation = useSession().me.role === "packplatz";
  return (
    <NoticeCard
      tone="check"
      testId="gehen-fehlt"
      title={forgotten.text}
      action={
        packingStation ? undefined : onAmend ? (
          <button
            type="button"
            data-testid="gehen-fehlt-nachtragen"
            className={AMEND_BUTTON}
            onClick={(e) => onAmend(forgotten.date, e.currentTarget)}
          >
            {CLOCK_CARD_TEXTS.amend}
          </button>
        ) : (
          <Link
            to={TIME_TRACKING_PATH}
            search={{ nachtragen: forgotten.date }}
            data-testid="gehen-fehlt-nachtragen"
            className={AMEND_BUTTON}
            onClick={onNavigate}
          >
            {CLOCK_CARD_TEXTS.amend}
          </Link>
        )
      }
    />
  );
}

/**
 * Stempeln mit Rückgängig (gemeinsam für Stempel-Karte und Stempel-Chip der Kopfzeile, Ticket 1.275): ein Command des
 * Servers (`POST /staff/me/clock-events`), danach die Rückgängig-Einblendung mit der Frist des Servers, Doppel-Tipp-Schutz
 * der Oberfläche und Freude-Moment nach „Gehen“. Keine zweite Stempel-Logik – Karte und Chip nutzen genau diesen Hook.
 */
export function useClockStamp(todayDate: string | undefined) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const [undoOffer, setUndoOffer] = useState<{
    key: number;
    eventId: string;
    text: string;
    seconds: number;
    until: Date;
  } | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [tapGuard, setTapGuard] = useState(false);
  // Freude-Moment: nach einem „Gehen“ dieser Sitzung, bis zum nächsten Stempel oder „Rückgängig“.
  const [farewell, setFarewell] = useState<string | null>(null);
  useEffect(() => {
    if (!tapGuard) return;
    const timer = window.setTimeout(
      () => setTapGuard(false),
      CLOCK_TAP_GUARD_MS,
    );
    return () => window.clearTimeout(timer);
  }, [tapGuard]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["staff"] });

  const stamp = useMutation({
    // Abschickzeit (Geräteuhr) mitgeben: die Frist zählt ab dem Stempeln, nicht ab der Antwort (langsame Verbindung).
    mutationFn: async (kind: ClockKind) => ({
      sentAt: Date.now(),
      ...(await apiFetch<RecordedClockEvent>(
        getToken,
        "/api/v1/staff/me/clock-events",
        { method: "POST", idempotencyKey: newIdempotencyKey(), body: { kind } },
      )),
    }),
    onSuccess: (result) => {
      setTapGuard(true);
      // Gehen nach Mitternacht bucht auf den Vortag – dann kein „heute …“.
      setFarewell(
        result.kind === "clock_out" &&
          result.changed &&
          result.workDate === todayDate
          ? result.workDate
          : null,
      );
      void refresh();
      if (!result.changed) return;
      // Frist vom Server (Einstellung „Rückgängig nach dem Stempeln“), gemessen an seinen Zeitpunkten – nie an der Gerätezeit.
      const seconds = Math.max(
        1,
        (new Date(result.undoableUntil).getTime() -
          new Date(result.occurredAt).getTime()) /
          1000,
      );
      // Hartes Ende = Server-Ende (`undoableUntil`), auf die Geräteuhr umgerechnet (die Testuhr des Servers kann weit
      // von der Gerätezeit abweichen); blendet auch aus, wenn der Zähler wegen Maus oder Fokus pausiert.
      // Ab dem Abschicken gerechnet, damit die Einblendung nie länger steht als der Server Rückgängig annimmt.
      const until = new Date(result.sentAt + seconds * 1000);
      const left = Math.max(1, (until.getTime() - Date.now()) / 1000);
      setUndoOffer({
        key: Date.now(),
        eventId: result.eventId,
        text: CLOCK_CARD_TEXTS.done(
          kindDone[result.kind],
          berlinTime(result.occurredAt),
        ),
        seconds: left,
        until,
      });
    },
    onError: () => void refresh(),
  });
  const undo = useMutation({
    mutationFn: (eventId: string) =>
      apiFetch(getToken, `/api/v1/staff/me/clock-events/${eventId}/undo`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey(),
        body: {},
      }),
    onSettled: () => {
      setFarewell(null);
      setUndoBusy(false);
      setUndoOffer(null);
      void refresh();
    },
  });

  const error = stamp.error ?? undo.error;
  const errorText =
    error instanceof ApiProblem
      ? error.status === 0
        ? CLOCK_CARD_TEXTS.offline
        : error.detail
      : error
        ? CLOCK_CARD_TEXTS.failed
        : null;
  const busy = stamp.isPending || undo.isPending || tapGuard;
  const press = (kind: ClockKind) => {
    setFarewell(null);
    stamp.reset();
    undo.reset();
    stamp.mutate(kind);
  };
  const toast = undoOffer ? (
    <UndoToast
      key={undoOffer.key}
      message={undoOffer.text}
      seconds={undoOffer.seconds}
      until={undoOffer.until}
      busy={undoBusy}
      onUndo={() => {
        setUndoBusy(true);
        undo.mutate(undoOffer.eventId);
      }}
      onDone={() => setUndoOffer(null)}
    />
  ) : null;
  return { press, busy, errorText, farewell, toast };
}

/**
 * Stempel-Karte (Story 22.1, EXPERIENCE §4.21, §10.2): ein Hauptknopf je Zustand, beschriftet mit der nächsten Handlung
 * (nicht da → „Kommen“, da → „Gehen“ und – mit Pause-Knopf – „Pause“, in Pause → „Pause beenden“), darüber der Satz
 * („Noch nicht gestempelt“, „Seit 07:30 da · heute 4:12 h“). Ein Tipp stempelt ohne Rückfrage; danach 10 s „Rückgängig“
 * (Frist vom Server). Die Zeit kommt vom Server; ohne Verbindung roter Satz, nichts wird gemerkt. Am Handy steht der
 * Hauptknopf fest unten, volle Breite, 56 px hoch. `optional`: ohne Mitarbeiter-Stammsatz gar nicht zeigen (Startseite).
 * Im Hinweis „Gestern fehlt ‚Gehen‘“ steht der Textlink „Nachtragen“ (Story 22.5): mit `onAmend` öffnet er das Formular
 * (Zeiterfassung › Heute), sonst führt er dorthin (`?nachtragen=<Tag>`, Startseite).
 * Ticket 1.284 (Vorbild Apple Fitness, Things 3): Ring Ist ÷ Tages-Soll als Messwert („4:12 h von 8:00 h“, ab Soll Haken
 * und „Soll erreicht · +0:15 h“; ohne Soll nur das Ist als Zahl), „Gestern fehlt ‚Gehen‘“ als Hinweis-Karte mit Knopf
 * „Nachtragen“ und nach „Gehen“ der Freude-Moment „Feierabend · heute 8:05 h“ mit Haken. Alle Minuten kommen vom Server
 * (`day.workMinutes`, `targetMinutes`, `balanceMinutes`); hier wird nur der Ring-Anteil gezeichnet. Keine Hinweise zum
 * Arbeitszeitgesetz [RUDOLF 22.10.2026].
 * `expanded` (nur Zeiterfassung › Heute, Nacharbeit 1.284): großer Ring (`size="lg"`) auf getönter Bühne (`brand-tile`)
 * mit Zustands-Chip („da seit 08:00“, ruhender Punkt, keine Dauer-Animation, EK5) und Hinweis-Karte zum nächsten Schritt
 * (`clockNextStep`). Ohne Mitarbeiter-Stammsatz steht dort die Hinweis-Karte „kein Mitarbeiter“ mit einem Hauptknopf.
 */
export function ClockCard({
  optional = false,
  expanded = false,
  hero = false,
  onAmend,
}: {
  optional?: boolean;
  expanded?: boolean;
  /**
   * Redesign R2 (Zeiterfassung › Heute): große Zahl mittig ohne Kartenfläche, Zustand darüber, Knöpfe darunter – der
   * Hauptknopf (erster Knopf des Servers) dunkelblau. Gleiche Daten und Knöpfe wie `expanded`.
   */
  hero?: boolean;
  onAmend?: (date: string, opener: HTMLElement) => void;
}) {
  const phone = useScreen() === "phone";
  const headingId = useId();
  const today = useStaffToday();
  const clock = useClockStamp(today.data?.day?.date);
  const data = today.data;
  if (optional && (!data || data.day === null)) return null;
  const errorText = clock.errorText;
  const farewell = clock.farewell;
  const buttons = data?.buttons ?? [];
  const knobs = buttons.map((b, i) => (
    <Button
      key={b.kind}
      variant={i === 0 ? "primary" : "secondary"}
      data-testid={i === 0 ? "stempel-hauptknopf" : "stempel-zweitknopf"}
      disabled={clock.busy}
      onClick={() => clock.press(b.kind)}
      className={
        phone
          ? "h-14 min-w-0 flex-1 pointer-coarse:h-14"
          : expanded || hero
            ? "h-12 px-space-6 text-lead"
            : undefined
      }
    >
      {b.label}
    </Button>
  ));

  const day = data?.day ?? null;
  const percent = day ? ringPercent(day.workMinutes, day.targetMinutes) : null;
  // „Erreicht“ nach dem Saldo des Servers (mit Rundung), damit Ring und Satz nie widersprechen.
  const reached = day !== null && percent !== null && day.balanceMinutes >= 0;
  // Nur für den Tag des Gehen-Stempels (Seite über Mitternacht offen: neuer Tag ohne Meldung).
  const showFarewell =
    farewell !== null &&
    day !== null &&
    day.date === farewell &&
    data?.state === "absent";
  const chip = data && day ? clockStateChip(data.state, day.entries) : null;
  const next =
    (expanded || hero) && data && day && !data.blocked && !data.forgotten && !showFarewell
      ? clockNextStep(data.state, day)
      : null;

  const loading = today.isPending ? (
    <p className="type-meta m-0 text-ink-3">Lädt …</p>
  ) : null;
  const loadError = today.error ? (
    <p role="alert" className="m-0 text-row text-clarify">
      {today.error instanceof ApiProblem && today.error.status === 0
        ? "Keine Verbindung."
        : today.error instanceof ApiProblem
          ? today.error.detail
          : CLOCK_CARD_TEXTS.failed}
    </p>
  ) : null;
  const sentence = data ? (
    <p
      role="status"
      data-testid="stempel-satz"
      className="m-0 text-row text-ink-2 [overflow-wrap:anywhere]"
    >
      {data.day ? data.sentence : data.blocked}
    </p>
  ) : null;
  const farewellLine =
    showFarewell && day ? (
      <p
        role="status"
        data-testid="stempel-feierabend"
        className="m-0 flex animate-fade-in items-center gap-space-2 text-lead font-medium text-ready"
      >
        <CircleCheck
          aria-hidden
          size={20}
          strokeWidth={2}
          className="flex-none"
        />
        {CLOCK_CARD_TEXTS.farewell(hoursText(day.workMinutes))}
      </p>
    ) : null;
  const notices = (
    <>
      {next ? (
        <NoticeCard
          tone="info"
          testId="stempel-naechster-schritt"
          title={next.title}
        >
          {next.text}
        </NoticeCard>
      ) : null}
      {data?.forgotten ? (
        <ForgottenNotice
          forgotten={data.forgotten}
          {...(onAmend ? { onAmend } : {})}
        />
      ) : null}
      {data?.day && data.blocked ? (
        <p
          data-testid="stempel-gesperrt"
          className="m-0 text-row text-ink-2 [overflow-wrap:anywhere]"
        >
          {data.blocked}
        </p>
      ) : null}
      {errorText ? (
        <p
          role="alert"
          data-testid="stempel-fehler"
          className="m-0 text-row text-clarify [overflow-wrap:anywhere]"
        >
          {errorText}
        </p>
      ) : null}
    </>
  );
  const phoneBar =
    knobs.length > 0 && phone ? (
      <PhoneActionBar>
        <div className="flex gap-space-3">{knobs}</div>
      </PhoneActionBar>
    ) : null;

  // Redesign OpenAI-Stil (N40): Zeiterfassung › Heute als ruhige Bühne – Zustand klein, die Zeit groß, darunter eine feine
  // Fortschrittslinie Ist ÷ Soll (Messwert) und die Knöpfe als runde Pillen. Gerechnet wird nichts (Minuten vom Server).
  if (expanded || hero) {
    return (
      <section
        aria-labelledby={headingId}
        data-testid="stempel-karte"
        data-state={data?.state ?? "loading"}
        data-look={hero ? "hero" : undefined}
        className={cn(
          "flex min-w-0 animate-fade-in flex-col gap-space-5",
          hero
            ? "items-center py-space-4 text-center tablet:py-space-8"
            : "card-box tablet:p-space-8",
        )}
      >
        <h2 id={headingId} className="sr-only">
          {CLOCK_CARD_TEXTS.title}
        </h2>
        {loading}
        {loadError}
        {data && !data.day ? <NoEmployeeNotice /> : null}
        {data && data.day ? (
          <div
            data-testid="stempel-buehne"
            className={cn(
              "flex min-w-0 flex-col gap-space-4",
              hero && "w-full measure",
            )}
          >
            <div
              className={cn(
                "flex min-w-0 flex-wrap items-center gap-space-3",
                hero && "justify-center",
              )}
            >
              {chip ? (
                <StateChip
                  tone={chip.tone}
                  testId="stempel-zustand"
                  className="bg-background"
                >
                  {chip.text}
                </StateChip>
              ) : null}
            </div>
            {day && !data.blocked && percent !== null ? (
              <p
                data-testid="stempel-ring-text"
                className={cn(
                  "m-0 flex min-w-0 flex-wrap items-baseline gap-x-space-3 gap-y-space-1 [overflow-wrap:anywhere]",
                  hero && "justify-center",
                )}
              >
                {reached ? (
                  <>
                    <span className="text-display leading-none font-semibold tracking-[-0.03em] text-ink tablet:text-hero-sm">
                      Soll erreicht
                    </span>
                    <span className="text-lead text-ready tabular-nums">
                      {` · ${signedHoursText(day.balanceMinutes)}`}
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={cn(
                        "leading-none font-semibold tracking-[-0.03em] text-ink tabular-nums",
                        hero
                          ? "text-hero-sm tablet:text-hero"
                          : "text-display tablet:text-hero-sm",
                      )}
                    >
                      {hoursText(day.workMinutes)}
                    </span>
                    <span className="text-lead text-ink-3 tabular-nums">
                      {` von ${hoursText(day.targetMinutes)}`}
                    </span>
                  </>
                )}
              </p>
            ) : null}
            {day && !data.blocked && percent === null ? (
              <p
                data-testid="stempel-ist"
                className="m-0 flex flex-wrap items-baseline gap-x-space-3 leading-none"
              >
                <span className="text-display font-semibold tracking-[-0.03em] text-ink tabular-nums tablet:text-hero-sm">
                  {hoursText(day.workMinutes)}
                </span>
                <span className="text-lead text-ink-3">
                  {CLOCK_CARD_TEXTS.today}
                </span>
              </p>
            ) : null}
            {day && !data.blocked && percent !== null ? (
              <ClockMeter
                percent={percent}
                done={reached}
                valueText={CLOCK_CARD_TEXTS.ofTarget(
                  hoursText(day.workMinutes),
                  hoursText(day.targetMinutes),
                )}
              />
            ) : null}
            {sentence}
          </div>
        ) : null}
        {farewellLine}
        {knobs.length > 0 && !phone ? (
          <div
            className={cn(
              "flex flex-wrap gap-space-3",
              hero && "justify-center",
            )}
          >
            {knobs}
          </div>
        ) : null}
        {hero ? (
          <div className="flex w-full measure flex-col gap-space-3 text-left">
            {notices}
          </div>
        ) : (
          notices
        )}
        {phoneBar}
        {clock.toast}
      </section>
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      data-testid="stempel-karte"
      data-state={data?.state ?? "loading"}
      className="card-box flex min-w-0 flex-col gap-space-4"
    >
      <div className="flex items-center gap-space-3">
        <IconCircle>
          <Clock size={16} strokeWidth={1.5} />
        </IconCircle>
        <TileHeading id={headingId}>{CLOCK_CARD_TEXTS.title}</TileHeading>
      </div>
      {loading}
      {loadError}
      {data && !data.day && !optional ? <NoEmployeeNotice /> : null}
      {data && (data.day || optional) ? (
        <div className="flex min-w-0 items-center gap-space-4">
          {day && !data.blocked && percent !== null ? (
            <ProgressRing
              testId="stempel-ring"
              size="md"
              track="line"
              value={day.workMinutes / day.targetMinutes}
              done={reached}
              label={CLOCK_CARD_TEXTS.ringLabel}
              meter={{
                valueText: CLOCK_CARD_TEXTS.ofTarget(
                  hoursText(day.workMinutes),
                  hoursText(day.targetMinutes),
                ),
                now: percent,
              }}
              caption={reached ? undefined : "% vom Soll"}
            >
              {percent}
            </ProgressRing>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col gap-space-2">
            {day && !data.blocked && percent !== null ? (
              <p
                data-testid="stempel-ring-text"
                className="m-0 text-tile-title font-medium text-ink tabular-nums [overflow-wrap:anywhere]"
              >
                {reached
                  ? CLOCK_CARD_TEXTS.reached(
                      signedHoursText(day.balanceMinutes),
                    )
                  : CLOCK_CARD_TEXTS.ofTarget(
                      hoursText(day.workMinutes),
                      hoursText(day.targetMinutes),
                    )}
              </p>
            ) : null}
            {day && !data.blocked && percent === null ? (
              <p
                data-testid="stempel-ist"
                className="m-0 flex flex-wrap items-baseline gap-x-space-2 leading-none"
              >
                <span className="text-kpi font-medium text-ink tabular-nums">
                  {hoursText(day.workMinutes)}
                </span>
                <span className="text-body text-ink-3">
                  {CLOCK_CARD_TEXTS.today}
                </span>
              </p>
            ) : null}
            {sentence}
          </div>
        </div>
      ) : null}
      {farewellLine}
      {notices}
      {knobs.length > 0 && !phone ? (
        <div className="flex flex-wrap gap-space-3">{knobs}</div>
      ) : null}
      {phoneBar}
      {clock.toast}
    </section>
  );
}

/**
 * Feine Fortschrittslinie Ist ÷ Tages-Soll (Redesign N40, ersetzt auf Heute den großen Ring): Messwert mit vorgelesenem
 * Wert, Füllung im TILL-Akzent, ab Soll voll in der Erledigt-Farbe. Nur die Breite aus der Prozentzahl des Servers.
 */
function ClockMeter({
  percent,
  done,
  valueText,
}: {
  percent: number;
  done: boolean;
  valueText: string;
}) {
  const [shown, setShown] = useState(0);
  const target = done ? 100 : Math.min(100, Math.max(0, percent));
  // Erst leer, dann im nächsten Bild füllen: der Übergang läuft auch beim ersten Erscheinen (reduzierte Bewegung: global 0 s).
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setShown(target));
    return () => window.cancelAnimationFrame(frame);
  }, [target]);
  return (
    <div className="flex min-w-0 items-center gap-space-3">
      <div
        role="meter"
        data-testid="stempel-ring"
        data-done={done ? "" : undefined}
        aria-label={CLOCK_CARD_TEXTS.ringLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, percent)}
        aria-valuetext={valueText}
        className="h-space-2 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-card-2"
      >
        <div
          data-ring-fill=""
          style={{ width: `${shown}%` }}
          className={cn(
            "h-full rounded-full transition-[width] duration-(--motion-slow) ease-(--motion-ease)",
            done ? "bg-ready" : "bg-brand",
          )}
        />
      </div>
      <span className="flex-none text-body text-ink-3 tabular-nums">
        {`${percent} %`}
      </span>
    </div>
  );
}
